import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, Plus, Pencil, Trash2, ArrowUpRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const CATEGORIES = ["Bedding", "Curtain", "Table Linen", "Placemat", "Throw", "Pillow", "Other"];

export default function StandardColorCards() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterBrand, setFilterBrand] = useState("all");
  const [search, setSearch] = useState("");
  const [brandsList, setBrandsList] = useState([]);
  const [productsList, setProductsList] = useState([]);
  const [templatesList, setTemplatesList] = useState([]);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ productId: "", templateId: "", brandId: "", code: "", category: "" });
  const [creating, setCreating] = useState(false);

  const [editCard, setEditCard] = useState(null);
  const [editForm, setEditForm] = useState({ code: "", category: "" });
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    api.get("/brands", { params: { status: "Active" } }).then(({ data }) => setBrandsList(data));
    api.get("/products", { params: { status: "Active" } }).then(({ data }) => setProductsList(data));
    api.get("/templates", { params: { status: "Active" } }).then(({ data }) => setTemplatesList(data));
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/standard-cards", { params: { category: filterCategory, brandId: filterBrand, search } });
      setCards(data);
    } catch (e) {
      toast.error(t("std.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filterCategory, filterBrand]);
  useEffect(() => { const tm = setTimeout(load, 300); return () => clearTimeout(tm); }, [search]);

  const openCreate = () => {
    setCreateForm({ productId: "", templateId: "", brandId: "", code: "", category: "" });
    setCreateOpen(true);
  };

  const onCreateProductChange = (productId) => {
    const p = productsList.find((x) => x.id === productId);
    setCreateForm((f) => ({ ...f, productId, category: p?.category || f.category }));
  };

  const handleCreate = async () => {
    if (!createForm.code.trim()) { toast.error(t("std.codeRequired")); return; }
    if (!(createForm.productId && createForm.templateId && createForm.brandId)) { toast.error(t("std.selectPTB")); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/standard-cards", createForm);
      toast.success(t("std.created", { code: data.standardCardCode }));
      setCreateOpen(false);
      navigate(`/designer/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const openEditMeta = (card) => {
    setEditCard(card);
    setEditForm({ code: card.standardCardCode || "", category: card.category || "" });
  };

  const handleSaveEdit = async () => {
    if (!editForm.code.trim()) { toast.error(t("std.codeEmpty")); return; }
    setSavingEdit(true);
    try {
      await api.put(`/standard-cards/${editCard.id}`, { code: editForm.code.trim(), category: editForm.category });
      toast.success(t("std.updated"));
      setEditCard(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/designs/${id}`);
      toast.success(t("std.deleted"));
      load();
    } catch (e) {
      toast.error(t("std.deleteFailed"));
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-8 py-10" data-testid="standard-cards-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-700 text-slate-900">{t("std.title")}</h1>
          <p className="mt-1.5 text-sm text-slate-500">{t("std.subtitle")}</p>
        </div>
        {isAdmin && (
          <Button className="bhf-btn-primary gap-1.5 border-0 text-white" onClick={openCreate} data-testid="add-standard-card-button">
            <Plus className="h-4 w-4" /> {t("std.addCard")}
          </Button>
        )}
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9" placeholder={t("std.searchPlaceholder")} value={search} onChange={(e) => setSearch(e.target.value)} data-testid="standard-card-search-input" />
        </div>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-[160px]" data-testid="standard-card-category-filter"><SelectValue placeholder={t("std.categoryPh")} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("std.allCategories")}</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterBrand} onValueChange={setFilterBrand}>
          <SelectTrigger className="w-[160px]" data-testid="standard-card-brand-filter"><SelectValue placeholder={t("std.brandPh")} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("std.allBrands")}</SelectItem>
            {brandsList.map((b) => <SelectItem key={b.id} value={b.id}>{b.brandName}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="standard-cards-grid">
        {cards.map((c) => (
          <div key={c.id} className="bhf-card overflow-hidden" data-testid={`standard-card-${c.id}`}>
            <div className="aspect-[4/3] w-full overflow-hidden bg-slate-100">
              {c.previewImage?.url ? <img src={fileUrl(c.previewImage)} alt={c.standardCardCode} className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-xs text-slate-400">{t("common.noPreview")}</div>}
            </div>
            <div className="p-3.5">
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono-bhf text-[11px] font-semibold text-indigo-600 truncate">{c.standardCardCode}</span>
                <Badge className="bg-slate-100 text-slate-600 shrink-0">{c.category || "—"}</Badge>
              </div>
              <div className="mt-1 truncate text-sm font-semibold text-slate-800">{c.productName || t("common.untitled")}</div>
              <div className="mt-0.5 truncate text-xs text-slate-500">{c.brandName || "—"}</div>
              {isAdmin && (
              <div className="mt-3 flex gap-1.5">
                <Button size="sm" variant="outline" className="flex-1 gap-1" onClick={() => navigate(`/designer/${c.id}`)} data-testid={`standard-card-edit-design-${c.id}`}>
                  <ArrowUpRight className="h-3.5 w-3.5" /> {t("std.editDesign")}
                </Button>
                <Button size="sm" variant="outline" onClick={() => openEditMeta(c)} data-testid={`standard-card-edit-meta-${c.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="outline" className="text-red-500 hover:text-red-600" data-testid={`standard-card-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t("std.deleteTitle")}</AlertDialogTitle>
                      <AlertDialogDescription>{t("std.deleteDesc", { code: c.standardCardCode })}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                      <AlertDialogAction onClick={() => handleDelete(c.id)} data-testid={`standard-card-confirm-delete-${c.id}`}>{t("common.delete")}</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
              )}
            </div>
          </div>
        ))}
        {!loading && cards.length === 0 && (
          <div className="col-span-full rounded-lg border border-dashed border-slate-200 p-8 text-center text-sm text-slate-400" data-testid="no-standard-cards">
            {t("std.noCards")}
          </div>
        )}
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="standard-card-create-modal">
          <DialogHeader>
            <DialogTitle>{t("std.createTitle")}</DialogTitle>
            <DialogDescription>{t("std.createDesc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs text-slate-500">{t("std.code")}</Label>
              <Input value={createForm.code} onChange={(e) => setCreateForm((f) => ({ ...f, code: e.target.value }))} placeholder={t("std.searchPlaceholder")} data-testid="standard-card-code-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">{t("std.product")}</Label>
              <Select value={createForm.productId} onValueChange={onCreateProductChange}>
                <SelectTrigger data-testid="standard-card-product-select"><SelectValue placeholder={t("std.selectProduct")} /></SelectTrigger>
                <SelectContent>
                  {productsList.map((p) => <SelectItem key={p.id} value={p.id}>{p.sku} · {p.productName}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">{t("std.template")}</Label>
              <Select value={createForm.templateId} onValueChange={(v) => setCreateForm((f) => ({ ...f, templateId: v }))}>
                <SelectTrigger data-testid="standard-card-template-select"><SelectValue placeholder={t("std.selectTemplate")} /></SelectTrigger>
                <SelectContent>
                  {templatesList.map((tpl) => <SelectItem key={tpl.id} value={tpl.id}>{tpl.templateName || tpl.templateCode}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">{t("std.brand")}</Label>
              <Select value={createForm.brandId} onValueChange={(v) => setCreateForm((f) => ({ ...f, brandId: v }))}>
                <SelectTrigger data-testid="standard-card-brand-select"><SelectValue placeholder={t("std.selectBrand")} /></SelectTrigger>
                <SelectContent>
                  {brandsList.map((b) => <SelectItem key={b.id} value={b.id}>{b.brandName}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">{t("std.category")}</Label>
              <Select value={createForm.category} onValueChange={(v) => setCreateForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="standard-card-category-select"><SelectValue placeholder={t("std.selectCategory")} /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} data-testid="standard-card-create-cancel">{t("common.cancel")}</Button>
            <Button className="bhf-btn-primary border-0 text-white" onClick={handleCreate} disabled={creating} data-testid="standard-card-create-submit">
              {creating ? t("std.creating") : t("std.createSubmit")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editCard} onOpenChange={(open) => !open && setEditCard(null)}>
        <DialogContent data-testid="standard-card-edit-modal">
          <DialogHeader>
            <DialogTitle>{t("std.editTitle")}</DialogTitle>
            <DialogDescription>{t("std.editDesc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs text-slate-500">{t("std.code")}</Label>
              <Input value={editForm.code} onChange={(e) => setEditForm((f) => ({ ...f, code: e.target.value }))} data-testid="standard-card-edit-code-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">{t("std.category")}</Label>
              <Select value={editForm.category} onValueChange={(v) => setEditForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="standard-card-edit-category-select"><SelectValue placeholder={t("std.selectCategory")} /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditCard(null)} data-testid="standard-card-edit-cancel">{t("common.cancel")}</Button>
            <Button className="bhf-btn-primary border-0 text-white" onClick={handleSaveEdit} disabled={savingEdit} data-testid="standard-card-edit-save">
              {savingEdit ? t("common.saving") : t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
